import { useCallback, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ThunkAction, ThunkDispatch } from 'redux-thunk';
import { AnyAction } from 'redux';
import { useEnvironment } from '@wix/yoshi-flow-editor';
import { useStyles } from '@wix/tpa-settings/react';
import { experimentsStore } from '@wix/wix-vod-shared/common';
import { stylesParams } from '@wix/wix-vod-constants/app-settings';
import { OOI_EDITOR_FLOW_SPEC } from '../constants/experiments';
import { useIds } from './useIds';
import { RootState } from '../redux/root-state';
import { ThunkExtra } from '../redux/redux.types';
import { setAppSettings } from '../redux/actions/app-settings';

type ReinitIds = { channelId: string; videoId: string | null };
// Desktop and mobile have their own reinitializeWidget (each drives its own setInitialState),
// so the concrete thunk is injected by the caller instead of importing a single form factor's.
// Both are async thunks that resolve when the reinit completes, so dispatch()ing one yields
// a Promise<void> the drain loop can await.
type ReinitializeWidget = (
  ids: ReinitIds,
) => ThunkAction<Promise<void>, RootState, ThunkExtra, AnyAction>;

export function useConfigBridge(reinitializeWidget: ReinitializeWidget) {
  const { isEditor } = useEnvironment();
  const styles = useStyles();
  const { channelId, videoId } = useIds();
  const dispatch =
    useDispatch<ThunkDispatch<RootState, ThunkExtra, AnyAction>>();

  // --- Style-param branch ---
  // Only channelLayout is synced here; the rest of styleParams still reach Redux via
  // widgetWrapper's `prevProps.styleParams !== styleParams` diff (updateAppSettings).
  // channelLayout needs this extra value-diffing path because in OOI the widgetWrapper
  // prop reference-diff is unreliable for it. Read it as a primitive so React's Object.is
  // dep-comparison catches changes even if the styles object reference is memoized/stable
  // — mirroring how useIds() exposes channelId/videoId as primitive strings.
  const channelLayout = styles.get(stylesParams.channelLayout) as
    | number
    | undefined;

  useEffect(() => {
    if (!isEditor || channelLayout === undefined) {
      return;
    }
    dispatch(setAppSettings({ numbers: { channelLayout } }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditor, channelLayout]);

  // --- ID branch (FT-gated: classic reloads, OOI reinitializes) ---
  const prevChannelIdRef = useRef<string | null | undefined>(undefined);
  const prevVideoIdRef = useRef<string | null | undefined>(undefined);
  const reinitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // pendingIdsRef is a one-slot "latest requested selection" — not a queue. Rapid channel
  // switches overwrite it, so only the final selection survives to be reinitialized.
  const pendingIdsRef = useRef<ReinitIds | null>(null);
  // Guards against two reinits running at once: only the first caller starts the drain
  // loop; later callers just update pendingIdsRef and let the running loop pick it up.
  const reinitInFlight = useRef(false);

  // FT off (control group) → classic reload on id change; FT on → OOI in-place reinit.
  const isOoi = experimentsStore.enabled(OOI_EDITOR_FLOW_SPEC);

  // Single-worker drain: reinitialize whatever ids are in the slot, then re-check the slot
  // in case a switch landed mid-reinit, and repeat until it's empty. Serializes reinits
  // (never two at once) and is latest-wins (intermediate selections are skipped).
  const drainReinitQueue = useCallback(async () => {
    // Already draining — the running loop will observe the ids the caller just stored.
    if (reinitInFlight.current) {
      return;
    }
    reinitInFlight.current = true;
    try {
      // Seed from the slot; loop only while there's work queued.
      let ids = pendingIdsRef.current;
      while (ids) {
        // Claim the slot before awaiting so we can tell if a *new* switch arrives during
        // the reinit (it would set pendingIdsRef non-null again).
        pendingIdsRef.current = null;
        await dispatch(reinitializeWidget(ids));
        // Re-read: non-null means a switch happened mid-reinit → run once more.
        ids = pendingIdsRef.current;
      }
    } finally {
      reinitInFlight.current = false;
    }
  }, [dispatch, reinitializeWidget]);

  useEffect(() => {
    if (!isEditor) {
      return;
    }
    if (
      prevChannelIdRef.current === undefined &&
      prevVideoIdRef.current === undefined
    ) {
      // First render — seed the refs, no dispatch
      prevChannelIdRef.current = channelId;
      prevVideoIdRef.current = videoId;
      return;
    }
    if (
      channelId === prevChannelIdRef.current &&
      videoId === prevVideoIdRef.current
    ) {
      return;
    }
    prevChannelIdRef.current = channelId;
    prevVideoIdRef.current = videoId;

    if (!isOoi) {
      window.location.reload();
      return;
    }

    if (reinitTimerRef.current) {
      clearTimeout(reinitTimerRef.current);
    }
    reinitTimerRef.current = setTimeout(() => {
      // Record the latest requested selection, then kick the drain. If a reinit is already
      // running, drainReinitQueue no-ops and the running loop picks these ids up when it
      // re-reads the slot.
      pendingIdsRef.current = { channelId, videoId };
      void drainReinitQueue();
    }, 150);

    return () => {
      if (reinitTimerRef.current) {
        clearTimeout(reinitTimerRef.current);
      }
    };
  }, [isEditor, isOoi, channelId, videoId, drainReinitQueue]);
}
